import { Controller } from "@hotwired/stimulus"

// Phase 2.7 Tier 4 — AI Minute Draft modal controller.
//
// Two phases share the same controller scope:
//   1. compose: textarea + live cost preview + "Extract" button.
//   2. suggestions: accept/edit/dismiss inbox identical to the § 2.6
//      candidate-risk inbox. The PM accepts each row individually
//      (after editing in-place if needed). Dismissed rows just fire a
//      tracking event — they were never persisted.
//
// Output is *never* auto-saved (Resolved Decision #12). The PM must
// click Accept on every row they want kept.
export default class extends Controller {
  static targets = [
    "composeStep", "suggestionsStep",
    "transcript", "charsLabel", "tokenLabel", "costLabel",
    "rateLabel", "truncationWarning",
    "extractButton", "extractLabel",
    "resultCount", "costFinal", "tokensFinal",
    "suggestionsList", "rowTemplate",
    "dropZone", "fileInput", "fileName"
  ]

  static values = {
    transcriptFileUrl: String,
    previewUrl: String,
    extractUrl: String,
    acceptUrl: String,
    dismissUrl: String
  }

  connect() {
    this._previewTimer = null
    this._csrf = document.querySelector("meta[name='csrf-token']")?.content || ""
    this._lastPreview = null
    this.previewDebounced()
  }

  // ── A transcript file instead of pasting (K3-9, 2026-10-08) ───────
  // Teams / Zoom .vtt or .docx, Plaud .txt…: the server reads it into
  // plain text, the box fills, and the cost preview runs as if pasted.

  dragOver(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.add("border-violet-500", "bg-violet-100")
  }

  dragLeave(event) {
    event.preventDefault()
    if (this.dropZoneTarget.contains(event.relatedTarget)) return
    this.dropZoneTarget.classList.remove("border-violet-500", "bg-violet-100")
  }

  drop(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove("border-violet-500", "bg-violet-100")
    const file = event.dataTransfer?.files?.[0]
    if (file) this._readTranscriptFile(file)
  }

  fileChosen(event) {
    const file = event.target.files?.[0]
    if (file) this._readTranscriptFile(file)
    event.target.value = ""
  }

  async _readTranscriptFile(file) {
    const label = this.fileNameTarget
    const messages = this._fileMessages()
    label.hidden = false
    label.classList.remove("text-red-600")
    label.textContent = messages.reading.replace("%{name}", file.name)
    const body = new FormData()
    body.append("file", file)
    try {
      const res = await fetch(this.transcriptFileUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrf, "Accept": "application/json" },
        body
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || messages.failed)
      this.transcriptTarget.value = data.text
      label.textContent = messages.done.replace("%{name}", data.name).replace("%{chars}", Number(data.chars).toLocaleString()) + (data.kept ? messages.kept : "")
      this.previewDebounced()
    } catch (e) {
      label.classList.add("text-red-600")
      label.textContent = e.message || messages.failed
    }
  }

  _fileMessages() {
    try { return JSON.parse(this.dropZoneTarget.dataset.messages || "{}") } catch (_e) { return {} }
  }

  previewDebounced() {
    clearTimeout(this._previewTimer)
    this._previewTimer = setTimeout(() => this.preview(), 250)
  }

  async preview() {
    const transcript = this.transcriptTarget.value
    this.charsLabelTarget.textContent = transcript.length.toLocaleString()
    if (transcript.trim().length < 50) {
      this.tokenLabelTarget.textContent = "0"
      this.costLabelTarget.textContent = "$0.00"
      this.truncationWarningTarget.classList.add("hidden")
      this.extractButtonTarget.disabled = true
      return
    }

    try {
      const res = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this._csrf,
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({ transcript }),
        credentials: "same-origin"
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      this._lastPreview = data
      this.tokenLabelTarget.textContent = data.estimated_tokens.toLocaleString()
      this.costLabelTarget.textContent = `$${data.estimated_cost_usd.toFixed(4)}`
      this.truncationWarningTarget.classList.toggle("hidden", !data.transcript_truncated)
      const rl = data.rate_limit
      this.rateLabelTarget.textContent = `${rl.remaining} of ${rl.limit} drafts remaining this hour`
      this.extractButtonTarget.disabled = !rl.allowed
    } catch (err) {
      console.warn("[meeting-ai-draft] preview failed", err)
    }
  }

  async extract() {
    const transcript = this.transcriptTarget.value
    if (transcript.trim().length < 50) return

    this.extractButtonTarget.disabled = true
    this.extractLabelTarget.textContent = "Extracting…"

    try {
      const res = await fetch(this.extractUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this._csrf,
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({ transcript }),
        credentials: "same-origin"
      })
      const data = await res.json()
      if (!res.ok || !data.ok) {
        this._showToast({ message: data.error || `Extract failed (${res.status})`, type: "error" })
        return
      }

      this._renderSuggestions(data)
    } catch (err) {
      console.error("[meeting-ai-draft] extract failed", err)
      this._showToast({ message: "Extract failed — try again.", type: "error" })
    } finally {
      this.extractLabelTarget.textContent = "Extract"
      this.extractButtonTarget.disabled = false
    }
  }

  backToCompose() {
    this.suggestionsStepTarget.classList.add("hidden")
    this.composeStepTarget.classList.remove("hidden")
  }

  _renderSuggestions(data) {
    this.suggestionsListTarget.innerHTML = ""
    this.resultCountTarget.textContent = data.suggestions.length
    this.costFinalTarget.textContent = `$${(data.cost_usd || 0).toFixed(4)}`
    this.tokensFinalTarget.textContent = `${data.total_token_bucket} tokens`

    if (data.suggestions.length === 0) {
      const empty = document.createElement("div")
      empty.className = "px-4 py-6 text-[13px] text-gray-500 text-center"
      empty.textContent = "AI didn't find anything to extract — paste a longer transcript or check the content."
      this.suggestionsListTarget.appendChild(empty)
    } else {
      data.suggestions.forEach((s, idx) => {
        const node = this._buildRow(s, idx)
        this.suggestionsListTarget.appendChild(node)
      })
    }

    this.composeStepTarget.classList.add("hidden")
    this.suggestionsStepTarget.classList.remove("hidden")
  }

  _buildRow(s, idx) {
    const tpl = this.rowTemplateTarget.content.cloneNode(true)
    const wrapper = tpl.querySelector("[data-suggestion-row]")
    wrapper.dataset.idx = idx
    wrapper.dataset.original = JSON.stringify(s)

    const kindChip = wrapper.querySelector("[data-row-target='kindChip']")
    kindChip.textContent = s.item_kind
    kindChip.classList.add(...this._kindChipClasses(s.item_kind))

    const impactChip = wrapper.querySelector("[data-row-target='impactChip']")
    if (s.item_kind === "decision" && s.impact_tag) {
      impactChip.textContent = `Impact: ${s.impact_tag}`
    } else {
      impactChip.classList.add("hidden")
    }

    wrapper.querySelector("[data-row-target='topic']").value = s.topic_title || "General"
    wrapper.querySelector("[data-row-target='subject']").value = s.item_subject || ""
    wrapper.querySelector("[data-row-target='notes']").value = s.item_notes || ""

    if (s.due_date_estimate) {
      const dueChip = wrapper.querySelector("[data-row-target='dueChip']")
      dueChip.classList.remove("hidden")
      wrapper.querySelector("[data-row-target='dueLabel']").textContent = s.due_date_estimate
    }
    if (s.responsible_name) {
      const ch = wrapper.querySelector("[data-row-target='responsibleChip']")
      ch.classList.remove("hidden")
      wrapper.querySelector("[data-row-target='responsibleLabel']").textContent = s.responsible_name
    }
    if (s.decided_by_name) {
      const ch = wrapper.querySelector("[data-row-target='decidedByChip']")
      ch.classList.remove("hidden")
      wrapper.querySelector("[data-row-target='decidedByLabel']").textContent = s.decided_by_name
    }

    return wrapper
  }

  _kindChipClasses(kind) {
    switch (kind) {
      case "decision": return ["bg-violet-100", "text-violet-800"]
      case "action":   return ["bg-amber-100", "text-amber-800"]
      case "informational": return ["bg-gray-100", "text-gray-700"]
      default:         return ["bg-blue-100", "text-blue-800"]
    }
  }

  async acceptRow(event) {
    const row = event.currentTarget.closest("[data-suggestion-row]")
    const original = JSON.parse(row.dataset.original || "{}")
    const topic = row.querySelector("[data-row-target='topic']").value.trim()
    const subject = row.querySelector("[data-row-target='subject']").value.trim()
    const notes = row.querySelector("[data-row-target='notes']").value.trim()

    if (!subject) {
      this._showToast({ message: "Subject is required.", type: "error" })
      return
    }

    const edited = (
      topic !== (original.topic_title || "General") ||
      subject !== (original.item_subject || "") ||
      notes !== (original.item_notes || "")
    )

    const buttons = row.querySelectorAll("button")
    buttons.forEach(b => b.disabled = true)

    try {
      const res = await fetch(this.acceptUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this._csrf,
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          edited: edited,
          suggestion: {
            topic_title: topic,
            item_kind: original.item_kind,
            item_subject: subject,
            item_notes: notes,
            impact_tag: original.impact_tag,
            due_date_estimate: original.due_date_estimate,
            responsible_name: original.responsible_name,
            decided_by_name: original.decided_by_name,
            importance: original.importance,
            said_at: original.said_at,
            progress: original.progress
          }
        }),
        credentials: "same-origin"
      })
      const data = await res.json()
      if (!res.ok || !data.ok) {
        this._showToast({ message: data.error || "Could not accept item.", type: "error" })
        buttons.forEach(b => b.disabled = false)
        return
      }

      const status = row.querySelector("[data-row-target='status']")
      status.textContent = `Accepted — saved as item #${data.meeting_item_id}.`
      status.classList.remove("hidden")
      row.classList.add("opacity-60")
    } catch (err) {
      console.error("[meeting-ai-draft] accept failed", err)
      this._showToast({ message: "Accept failed — try again.", type: "error" })
      buttons.forEach(b => b.disabled = false)
    }
  }

  async dismissRow(event) {
    const row = event.currentTarget.closest("[data-suggestion-row]")
    const original = JSON.parse(row.dataset.original || "{}")

    try {
      await fetch(this.dismissUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this._csrf,
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({ item_kind: original.item_kind }),
        credentials: "same-origin"
      })
    } catch (err) {
      console.warn("[meeting-ai-draft] dismiss tracking failed", err)
    }

    const status = row.querySelector("[data-row-target='status']")
    status.textContent = "Dismissed."
    status.classList.remove("hidden")
    status.classList.remove("text-emerald-700")
    status.classList.add("text-gray-400")
    row.classList.add("opacity-50")
    row.querySelectorAll("button").forEach(b => b.disabled = true)
  }

  _showToast({ message, type = "success" }) {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
